import React, { useState, useEffect, memo } from "react";
import Modal from "react-modal";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { ProgressBar } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faLock,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";

import {
  sharedConstants,
  galleryConstants,
  globalSystemConstants,
} from "../../../constants";
import { customerDashboardActions } from "../../../redux/actions";
import FilePreview from "./FilePreview";
import CheckBox from "../Fields/CheckBox";

function ViewGallery({
  files,
  setFiles,
  selectedFiles,
  isImageDeleted,
  setSelectedFiles,
  deleteFromGallery,
  progress,
  showHearthIcon,
  ...props
}) {
  const { t } = useTranslation("common");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAllFilesSelected, setIsAllFilesSelected] = useState(false);
  // const [totalSelectedFiles, setTotalSelectedFiles] = useState("");
  const [activeTab, setActiveTab] = useState(galleryConstants.GALLERY_IMAGES);
  const [viewFiles, setViewFiles] = useState(files);
  const handleDeleteSelected = () => {
    props.handleDelete(selectedFiles);
    // setTotalSelectedFiles("");
    setIsAllFilesSelected(false);
  };

  const handleDownloadSelected = async () => {
    if (selectedFiles.length > 1) {
      props.downloadSelectedFiles({
        ids: selectedFiles.map((file) => file.id),
        email: props.userName,
      });
      // setTotalSelectedFiles("");
      setSelectedFiles([]);
    } else {
      let file = selectedFiles[0];
      handleSingleDownload(file);
    }
  };

  const handleSingleDownload = async (file) => {
    props.activateSpinner();
    try {
      const fileExtenssion = file?.url.split(".").pop();
      const fileName = file?.file?.name.replace(/\.$/, "");
      const fileUrl =
        fileName !== "undefined"
          ? window.config.downloadSingleFileUrl +
            fileName +
            "." +
            fileExtenssion
          : file?.url;
      let modifiedBlob;
      const randomNumber = Math.floor(Math.random() * 100) + 1;
      if (file.url.endsWith(".webp")) {
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        modifiedBlob = new Blob([blob], { type: "image/webp" });
      } else if (file.url.endsWith(".bmp")) {
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        modifiedBlob = new Blob([blob], { type: "image/bmp" });
      } else if (file.url.endsWith(".jpeg")) {
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        modifiedBlob = new Blob([blob], { type: "image/jpeg" });
      } else {
        // For other file types, use the original Blob
        const response = await fetch(fileUrl);
        modifiedBlob = await response.blob();
      }
      const url = window.URL.createObjectURL(modifiedBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        fileName !== "undefined"
          ? `${fileName}.${fileExtenssion}`
          : randomNumber;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the temporary URL
      window.URL.revokeObjectURL(url);
      // setTotalSelectedFiles("");
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
    props.diActivateSpinner();
  };

  const handleSelect = (file) => {
    if (file.isLocked && props.isShareAllowed) {
    } else {
      const index = selectedFiles.findIndex((f) => f === file);
      if (index !== -1) {
        setSelectedFiles((prevSelectedFiles) =>
          prevSelectedFiles.filter((f) => f !== file)
        );
      } else {
        setSelectedFiles((prevSelectedFiles) => [...prevSelectedFiles, file]);
      }
    }
    // setTotalSelectedFiles("");
    setIsAllFilesSelected(false);
  };

  const handleSelectAll = () => {
    if (!isAllFilesSelected && props.isShareAllowed) {
      let filteredFiles = viewFiles.filter((x) => x.isLocked === false);
      setIsAllFilesSelected(true);
      // setTotalSelectedFiles(filteredFiles.length);
      setSelectedFiles(filteredFiles);
    } else if (isAllFilesSelected) {
      setIsAllFilesSelected(false);
      // setTotalSelectedFiles("");
      setSelectedFiles([]);
    } else {
      setIsAllFilesSelected(true);
      // setTotalSelectedFiles(viewFiles.length);
      setSelectedFiles(viewFiles);
    }
  };

  useEffect(() => {
    // Clean up object URLs when component unmounts
    return () => {
      viewFiles.forEach((file) => URL.revokeObjectURL(file.url));
    };
  }, [viewFiles]);

  const handleOpenModal = (index) => {
    setIsModalOpen(true);
    setCurrentImageIndex(index);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleLockSelected = () => {
    props.handleLock(selectedFiles);
  };

  useEffect(() => {
    if (props.isAsyncUploadCall) {
      let counter = 0;

      files.forEach((file) => {
        if (!file.hasOwnProperty("id")) {
          counter++;
        }
      });

      props.setCounterTotalFilesToUpload(counter);
    }
    // eslint-disable-next-line
  }, [files]);

  useEffect(() => {
    if (
      viewFiles.length > 0 &&
      activeTab === galleryConstants.GALLERY_IMAGES &&
      props.isTabsDisplay
    ) {
      setViewFiles(
        files?.filter((x) => x.galleryType === galleryConstants.GALLERY_IMAGES)
      );
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    handleGalleryFiles(activeTab, true);
    // eslint-disable-next-line
  }, [files]);

  const handleGalleryFiles = (tab, isChangedFile = false) => {
    if (activeTab !== tab || isChangedFile) {
      setActiveTab(tab);
      setIsAllFilesSelected(false);
      // setTotalSelectedFiles("");
      setSelectedFiles([]);
      if (tab === galleryConstants.GALLERY_REELS) {
        setViewFiles(
          files.filter(
            (x) =>
              x.galleryType === tab &&
              x.contentType === galleryConstants.CONTENT_TYPE_REELS
          )
        );
      } else {
        setViewFiles(
          files.filter(
            (x) =>
              x.galleryType === tab &&
              x.contentType === galleryConstants.CONTENT_TYPE_FILES
          )
        );
      }
    }
  };

  return (
    <>
      <div className="upload-gallery mt-2">
        <div
          className={`${
            props.isAuthenticated ? "file-buttons row mb-2 mx-0" : ""
          } bg-transparent`}
        >
          <div className="col-12 px-0 p-0">
            {progress > 0 && (
              <>
                {props.counterFilesUploaded <=
                  props.counterTotalFilesToUpload &&
                  props.isAsyncUploadCall && (
                    <>
                      <p className="p-0 m-0">
                        File:{" "}
                        {props.currentFileName.length > 50 ? (
                          <>
                            {props.currentFileName.slice(0, 42) +
                              "..." +
                              props.currentFileName.slice(-5)}
                          </>
                        ) : (
                          props.currentFileName
                        )}
                      </p>
                      <p className="p-0 m-0">
                        Size: {props.currentFileSize} Mbs
                      </p>
                      <p>
                        Uploading: {props.counterFilesUploaded} of{" "}
                        {props.counterTotalFilesToUpload}
                      </p>
                    </>
                  )}
                <ProgressBar
                  now={progress}
                  active
                  label={`${Number(progress).toFixed(0)}%`}
                />
              </>
            )}
          </div>
          {props.isTabsDisplay ? (
            <Box sx={{ width: "100%" }} className="p-0 mb-3 mt-2">
              <Box sx={{ borderBottom: 0, borderColor: "divider" }}>
                <Tabs
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                  value={activeTab}
                  aria-label="Tabs"
                  className="member-my-events-tabs access-gallery-tabs align-items-center"
                  sx={{
                    "& .MuiTab-root": {
                      minHeight: 25,
                      padding: "0px 0px",
                      margin: "0px 10px",
                      minWidth: "auto",
                      gap: 5,
                    },
                  }}
                >
                  <Tab
                    label="Images"
                    className="text ml-0"
                    onClick={() => {
                      handleGalleryFiles(galleryConstants.GALLERY_IMAGES);
                    }}
                    aria-selected={
                      activeTab === galleryConstants.GALLERY_IMAGES
                        ? true
                        : false
                    }
                  />{" "}
                  <div className="tab-divider"></div>
                  <Tab
                    label="Videos"
                    className="text"
                    onClick={() => {
                      handleGalleryFiles(galleryConstants.GALLERY_VIDEOS);
                    }}
                    aria-selected={
                      activeTab === galleryConstants.GALLERY_VIDEOS
                        ? true
                        : false
                    }
                  />{" "}
                  <div className="tab-divider"></div>
                  <Tab
                    label="Recap(z)"
                    className="text"
                    onClick={() => {
                      handleGalleryFiles(galleryConstants.GALLERY_REELS);
                    }}
                    aria-selected={
                      activeTab === galleryConstants.GALLERY_REELS
                        ? true
                        : false
                    }
                  />
                </Tabs>
              </Box>
            </Box>
          ) : (
            ""
          )}
          {props.isAuthenticated || props.isSBAuthenticated ? (
            <div className="col-12 pl-0 pr-0 d-flex pb-2">
              <CheckBox
                containerClass={"teal-checkbox pr-2"}
                labelText={
                  selectedFiles.length === 0 ? (
                    "select all"
                  ) : (
                    <span className="font-weight-bold">
                      {selectedFiles.length} selected
                    </span>
                  )
                }
                checked={isAllFilesSelected}
                setChecked={handleSelectAll}
                disabled={viewFiles.length === 0}
              />

              {props.isAllowDownload ? (
                <div
                  className={
                    selectedFiles.length === 0
                      ? "download-selected pr-2"
                      : "download-selected pr-2 cursor-pointer"
                  }
                  onClick={handleDownloadSelected}
                  disabled={selectedFiles.length === 0 || progress > 0}
                >
                  <FontAwesomeIcon icon={faDownload} className="clr-blue" />
                </div>
              ) : (
                ""
              )}

              {props.isAllowLock ? (
                <div
                  className={
                    selectedFiles.length === 0
                      ? "border-0 pr-2 bg-none"
                      : "border-0 pr-2 bg-none cursor-pointer"
                  }
                  onClick={handleLockSelected}
                  disabled={progress > 0}
                >
                  <FontAwesomeIcon icon={faLock} />
                </div>
              ) : (
                ""
              )}
              {!props.hideDeleteIcon && (
                <button
                  className="cursor-pointer border-0 bg-transparent"
                  onClick={handleDeleteSelected}
                  disabled={selectedFiles.length === 0 || progress > 0}
                >
                  <FontAwesomeIcon
                    icon={faTrashAlt}
                    color={
                      selectedFiles.length === 0 || progress > 0
                        ? "inherit"
                        : "#3e8993"
                    }
                  />
                </button>
              )}
            </div>
          ) : (
            ""
          )}
        </div>
        {(props.isReelUpload ? files : viewFiles)?.filter(
          (file) => file.url !== sharedConstants.IS_GALLERY_UPLOAD_PREVIEW
        ).length ? (
          <FilePreview
            files={props.isReelUpload ? files : viewFiles}
            selectedFiles={selectedFiles}
            showCheckbox={!isModalOpen}
            showPlayButton={!isModalOpen}
            showLockIcon={!isModalOpen}
            isModalOpen={isModalOpen}
            isModalAllowed={props.isModalAllowed}
            isAuthenticated={props.isAuthenticated}
            isSBAuthenticated={props.isSBAuthenticated}
            isShareAllowed={props.isShareAllowed}
            isAllowLock={props.isAllowLock}
            onFileSelect={handleSelect}
            onOpenModal={handleOpenModal}
            showHearthIcon={showHearthIcon}
            isFavoriteReadOnly={props.isFavoriteReadOnly}
            favoriteClipHandle={props.favoriteClipHandle}
          />
        ) : (
          <div className="d-flex justify-content-center pt-3">
            <p>{t("no_files_uploaded")}</p>
          </div>
        )}

        <Modal
          isOpen={isModalOpen}
          onRequestClose={handleCloseModal}
          contentLabel="Media Preview Modal"
          className="media-preview-modal"
          overlayClassName="media-preview-modal-overlay"
          bodyOpenClassName="gallery-modal-body-open"
        >
          <div className="modal-content">
            {viewFiles[currentImageIndex]?.file &&
              (viewFiles[currentImageIndex].file?.type.startsWith("image/") ? (
                <>
                  {!viewFiles[currentImageIndex].isLocked &&
                  !props.isSBAuthenticated ? (
                    <div
                      className="media-preview-download-icon"
                      onClick={() =>
                        handleSingleDownload(viewFiles[currentImageIndex])
                      }
                    >
                      <FontAwesomeIcon
                        icon={faDownload}
                        className="text-light"
                      />
                    </div>
                  ) : (
                    ""
                  )}
                  <img
                    src={
                      viewFiles[currentImageIndex]?.id
                        ? viewFiles[currentImageIndex].url
                        : URL.createObjectURL(viewFiles[currentImageIndex].file)
                    }
                    alt="Preview"
                    className="media-preview-element"
                  />
                </>
              ) : (
                <>
                  {!viewFiles[currentImageIndex].isLocked &&
                  !props.isSBAuthenticated ? (
                    <div
                      className="media-preview-download-icon"
                      onClick={() =>
                        handleSingleDownload(viewFiles[currentImageIndex])
                      }
                    >
                      <FontAwesomeIcon
                        icon={faDownload}
                        className="text-light"
                      />
                    </div>
                  ) : (
                    ""
                  )}
                  <video
                    src={
                      viewFiles[currentImageIndex]?.id
                        ? viewFiles[currentImageIndex].url
                        : URL.createObjectURL(viewFiles[currentImageIndex].file)
                    }
                    className="media-preview-element"
                    controls
                  />
                </>
              ))}

            <button onClick={handleCloseModal} className="modal-close-button">
              <img
                alt="Close Modal"
                className="media-preview-close-icon"
                src="/images/simpleCrossWhite.svg"
              />
            </button>
            <button
              className="arrow-button arrow-button--left"
              disabled={currentImageIndex === 0}
              onClick={() => setCurrentImageIndex((prevIndex) => prevIndex - 1)}
            >
              <img
                alt="Previous"
                className="media-preview-arrow-icon"
                src="/images/previousArrowButtonWhite.svg"
              />
            </button>
            <button
              className="arrow-button arrow-button--right"
              onClick={() => setCurrentImageIndex((prevIndex) => prevIndex + 1)}
              disabled={currentImageIndex === viewFiles.length - 1}
            >
              <img
                alt="Next"
                className="media-preview-arrow-icon"
                src="/images/nextArrowButtonWhite.svg"
              />
            </button>
          </div>
        </Modal>
      </div>
    </>
  );
}

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  userName: state.authentication.userName,
  isAuthenticated: state.authentication.isAuthenticated,
  isSBAuthenticated: state.shutterbAuthentication.isShutterbAuthenticated,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  downloadSelectedFiles: (eventIdsArr) =>
    dispatch(customerDashboardActions.downloadSelectedFiles(eventIdsArr)),
  activateSpinner: () =>
    dispatch({
      type: globalSystemConstants.DOWNLOAD_SPINNER_ACTIVATE,
      data: null,
    }),
  diActivateSpinner: () =>
    dispatch({
      type: globalSystemConstants.DOWNLOAD_SPINNER_DIACTIVATE,
      data: null,
    }),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(memo(ViewGallery)));
